import { useId } from 'react';
import styles from './Input.module.css';

/**
 * A labelled form row: label + control + optional error/hint text.
 * Wrap any control in it — <Input>, <Segmented>, a select, anything.
 */
export function Field({ label, hint, error, htmlFor, className = '', children }) {
  return (
    <div className={`${styles.field} ${className}`}>
      {label && (
        <label className={styles.label} htmlFor={htmlFor}>
          {label}
        </label>
      )}
      {children}
      {error ? (
        <p className={styles.error} role="alert">
          {error}
        </p>
      ) : (
        hint && <p className={styles.hint}>{hint}</p>
      )}
    </div>
  );
}

/** Standard text input — ink background, orange focus ring. */
export function Input({ invalid = false, className = '', ...rest }) {
  const classes = [styles.input, invalid && styles.invalid, className]
    .filter(Boolean)
    .join(' ');
  return <input className={classes} aria-invalid={invalid || undefined} {...rest} />;
}

/**
 * Convenience wrapper: a Field and an Input wired together with a shared id,
 * so clicking the label focuses the box.
 */
export function TextField({ label, hint, error, className, ...inputProps }) {
  const id = useId();
  return (
    <Field label={label} hint={hint} error={error} htmlFor={id} className={className}>
      <Input id={id} invalid={Boolean(error)} {...inputProps} />
    </Field>
  );
}
